import { useModalDarkContext } from "@/components/context/ModalDarkContext"
import { EasetechSpinnerProps } from "@/components/easetech-spinner/EasetechSpinner"
import AoiApprovalConfirmation from "@/components/modal-dark/modal-dark-platform-content/aoi-approval-confirmation/AoiApprovalConfirmation"
import AoiSubmittingConfirmation from "@/components/modal-dark/modal-dark-platform-content/aoi-submitting-confirmation/AoiSubmittingConfirmation"
import ArchiveNotice from "@/components/modal-dark/modal-dark-platform-content/archive-notice/ArchiveNotice"
import AssistanceSentNotice from "@/components/modal-dark/modal-dark-platform-content/assistance-request-sent.tsx/AssistanceSentNotice"
import AutoApprovalInfo from "@/components/modal-dark/modal-dark-platform-content/auto-approval-info/AutoApprovalInfo"
import ChoosePortal from "@/components/modal-dark/modal-dark-platform-content/choose-portal/ChoosePortal"
import ConfirmEmergency from "@/components/modal-dark/modal-dark-platform-content/confirm-emergency/ConfirmEmergency"
import DeleteConfirmation from "@/components/modal-dark/modal-dark-platform-content/delete-confirmation/DeleteConfirmation"
import EmailNotConfirmed from "@/components/modal-dark/modal-dark-platform-content/email-not-confirmed/EmailNotConfirmed"
import EmailSendingConfirmation from "@/components/modal-dark/modal-dark-platform-content/email-sending-confirmation/EmailSendingConfirmation"
import EmailTaken from "@/components/modal-dark/modal-dark-platform-content/email-taken/EmailTaken"
import ExpiredConfirmationLink from "@/components/modal-dark/modal-dark-platform-content/expired-confirmation-link/ExpiredConfirmationLink"
import KmzConversionNotice from "@/components/modal-dark/modal-dark-platform-content/kmz-conversion-notice/KmzConversionNotice"
import WayleaveRequirementsLevel from "@/components/modal-dark/modal-dark-platform-content/level-of-service/WayleaveRequirementsLevel"
import LicenseRequired from "@/components/modal-dark/modal-dark-platform-content/license-required/LicenseRequired"
import LicenceUpgradeRequest from "@/components/modal-dark/modal-dark-platform-content/license-upgrade-request/LicenseUpgradeRequest"
import LinkSuccessfulNotice from "@/components/modal-dark/modal-dark-platform-content/link-successful-notice/LinkSuccessfulNotice"
import OutgoingOnlyPortal from "@/components/modal-dark/modal-dark-platform-content/outgoing-only-portal/OutgoingOnlyPortal"
import PaymentOverdue from "@/components/modal-dark/modal-dark-platform-content/payment-overdue/PaymentOverdue"
import PaymentSuccess from "@/components/modal-dark/modal-dark-platform-content/payment-success/PaymentSuccess"
import PortalMismatch from "@/components/modal-dark/modal-dark-platform-content/portal-mismatch/PortalMismatch"
import StartNotice from "@/components/modal-dark/modal-dark-platform-content/start-notice/StartNotice"
import SubmittingConfirmation from "@/components/modal-dark/modal-dark-platform-content/submitting-confirmation/SubmittingConfirmation"
import SuccessAoiApproval from "@/components/modal-dark/modal-dark-platform-content/success-aoi-approval/SuccessAoiApproval"
import UploadGeometryNotice from "@/components/modal-dark/modal-dark-platform-content/upload-geometry-notice/UploadGeometryNotice"
import ZoomNotice from "@/components/modal-dark/modal-dark-platform-content/zoom-notice/ZoomNotice"
import AutoApprovalSuccess from "@/components/modal-dark/modal-dark-website-content/auto-approval-success/AutoApprovalSuccess"
import AutoApprovalTriggered from "@/components/modal-dark/modal-dark-website-content/auto-approval-triggered/AutoApprovalTriggered"
import DeliveryConfirmedNotice from "@/components/modal-dark/modal-dark-website-content/delivery-confirmed-notice/DeliveryConfirmedNotice"
import DeliverySentNotice from "@/components/modal-dark/modal-dark-website-content/delivery-sent-notice/DeliverySentNotice"
import FullReview from "@/components/modal-dark/modal-dark-website-content/full-review/FullReview"
import LoadingNotice from "@/components/modal-dark/modal-dark-website-content/loading-notice/LoadingNotice"
import PricingRequest from "@/components/modal-dark/modal-dark-website-content/pricing-request/PricingRequest"
import SuccessNotice from "@/components/modal-dark/modal-dark-website-content/success-notice/SuccessNotice"
import { FC, Suspense, lazy } from "react"

// Contents that pull in maps, PDF previews or file uploads load on first open, so the modal itself (and the small
// sign-in notices shown through it) stays out of their way and out of every page's first download.
const AssistanceInfo = lazy(
  () => import("@/components/modal-dark/modal-dark-platform-content/assistance-info/AssistanceInfo")
)
const DeliveryDetails = lazy(
  () => import("@/components/modal-dark/modal-dark-platform-content/delivery-details/DeliveryDetails")
)
const DeliveryReportPreview = lazy(
  () => import("@/components/modal-dark/modal-dark-platform-content/delivery-report-preview.tsx/DeliveryReportPreview")
)
const MapWebglNotice = lazy(
  () => import("@/components/modal-dark/modal-dark-platform-content/map-webgl-notice/MapWebglNotice")
)
const ProjectSizeNotice = lazy(
  () => import("@/components/modal-dark/modal-dark-platform-content/project-size-notice/ProjectSizeNotice")
)
const SingleSubmission = lazy(
  () => import("@/components/modal-dark/modal-dark-platform-content/single-submission/SingleSubmission")
)
const DeliveryConfirmation = lazy(
  () => import("@/components/modal-dark/modal-dark-website-content/delivery-confirmation/DeliveryConfirmation")
)
const GetQuote = lazy(() => import("@/components/modal-dark/modal-dark-website-content/get-quote/GetQuote"))
const MoreInfo = lazy(() => import("@/components/modal-dark/modal-dark-website-content/more-info/MoreInfo"))
const QuickContactForm = lazy(
  () => import("@/components/modal-dark/modal-dark-website-content/quick-contact-form/QuickContactForm")
)

const ModalDarkContent: FC = () => {
  const { modalDarkTypes, contentReviewCard, modalDarkContent } = useModalDarkContext()

  const modalDarkContentGenerator = () => {
    switch (modalDarkTypes) {
      case "assistance-info":
        return <AssistanceInfo />

      case "assistance-sent-notice":
        return <AssistanceSentNotice />

      case "delivery-report-preview":
        return <DeliveryReportPreview />

      case "delivery-details":
        return <DeliveryDetails />

      case "delivery-confirmed":
        return <DeliveryConfirmedNotice />

      case "link-successful-notice":
        return <LinkSuccessfulNotice />

      case "delivery-sent-notice":
        return <DeliverySentNotice />

      case "delivery-confirmation":
        return <DeliveryConfirmation />

      case "email-sending-confirmation":
        return <EmailSendingConfirmation />

      case "submitting-confirmation":
        return <SubmittingConfirmation />

      case "confirm-emergency":
        return <ConfirmEmergency />

      case "upload-geometry-notice":
        return <UploadGeometryNotice />

      case "project-size-notice":
        return <ProjectSizeNotice />

      case "archive-notice":
        return <ArchiveNotice />

      case "delete-confirmation":
        return <DeleteConfirmation />

      case "payment-success":
        return <PaymentSuccess />

      case "aoi-submitting-confirmation":
        return <AoiSubmittingConfirmation />

      case "aoi-approval-confirmation":
        return <AoiApprovalConfirmation />

      case "email-not-confirmed":
        return <EmailNotConfirmed />

      case "expired-confirmation-link":
        return <ExpiredConfirmationLink />

      case "email-taken":
        return <EmailTaken />

      case "success-aoi-approval":
        return <SuccessAoiApproval />

      case "choose-portal":
        return <ChoosePortal />

      case "portal-mismatch":
        return <PortalMismatch />

      case "outgoing-only-portal":
        return <OutgoingOnlyPortal />

      case "payment-overdue":
        return <PaymentOverdue />

      case "license-required":
        return <LicenseRequired />

      case "zoom-notice":
        return <ZoomNotice />

      case "map-webgl-notice":
        return <MapWebglNotice />

      case "start-notice":
        return <StartNotice />

      case "pricing-request":
        return <PricingRequest />

      case "success":
        return <SuccessNotice />

      case "auto-approval-triggered":
        return <AutoApprovalTriggered />

      case "auto-approval-success":
        return <AutoApprovalSuccess />

      case "auto-approval-info":
        return <AutoApprovalInfo />

      case "quick-contact":
        return <QuickContactForm />

      case "get-quote":
        return <GetQuote mode={"quote"} />

      case "more-info":
        return <MoreInfo />

      case "single-submission":
        return <SingleSubmission />

      case "level-of-service":
        return <WayleaveRequirementsLevel />

      case "kmz-conversion-notice":
        return <KmzConversionNotice />

      case "license-upgrade-request":
        return <LicenceUpgradeRequest />

      case "full-review":
        return <FullReview it={contentReviewCard} />

      case "loading-notice":
        return <LoadingNotice {...(modalDarkContent as EasetechSpinnerProps)} />

      default:
        return (
          <p style={{ color: "white", textAlign: "center" }}>
            {"We couldn’t display this information at the moment. Please refresh the page or try again later."}
          </p>
        )
    }
  }
  return (
    <div className={"modal-dark-content"}>
      <Suspense fallback={null}>{modalDarkContentGenerator()}</Suspense>
    </div>
  )
}

export default ModalDarkContent
